import { FilterRangeEndDatePicker, FilterRangeStartDatePicker } from "components/DatePicker/RangeDatePicker";
import { FilterDatePicker } from "components/DatePicker/SingleDatePicker";
import moment, { type Moment } from "moment";
import React, { useState } from "react";
import { getMonthsList, getQuartersList, getYearsList } from "./timeutils";

interface IProps {
  periodFilter: server.dto.PeriodFilterOptions
  onChange: (periodStart: Moment, periodEnd: Moment, closeFilter: boolean) => void

  initialStart: moment.Moment
  initialEnd: moment.Moment
}

interface ISubPeriodProps {
  onChange: (periodStart: Moment, periodEnd: Moment, closeFilter: boolean) => void

  initialStart: moment.Moment
  initialEnd: moment.Moment
}

const zeroMoment = moment.utc(0);

const PerYear = (props: ISubPeriodProps) => {
  const onChange = (selectedYear: number, closeFilter: boolean) => {
    const start = moment.utc(selectedYear, "YYYY").startOf("year");
    const end = start.clone().add(1, "year");

    props.onChange(start, end, closeFilter);
  };

  const years = getYearsList();
  const defaultYear = moment.utc(props.initialStart).year();

  return <select defaultValue={defaultYear.toString()} onChange={e => { onChange(+e.currentTarget.value, true); }}>
    {years.map((year) => (
      <option key={year} value={year}>{year}</option>
    ))}
  </select>;
};

export const PerQuarter = (props: ISubPeriodProps) => {
  const onChange = (value: number, closeFilter: boolean) => {
    const start = moment.utc(value);
    const end = start.clone().add(3, "months");

    props.onChange(start, end, closeFilter);
  };

  const quarters = getQuartersList();
  const defaultValue = moment.utc(props.initialStart).startOf("quarter").valueOf();

  return <select defaultValue={defaultValue.toString()} onChange={e => { onChange(+e.currentTarget.value, true); }}>
    {quarters.map(({ year, entries }) => (
      <optgroup key={`quarter${year}`} label={year.toString()}>
        {entries.map(({ label, value }) =>
          <option key={`quarter${value}`} value={value}>{label}</option>
        )}
      </optgroup>
    ))}
  </select>;
};

export const PerMonth = (props: ISubPeriodProps) => {
  const onChange = (value: number, closeFilter: boolean) => {
    if (value === 0) {
      props.onChange(zeroMoment, zeroMoment, closeFilter);
      return;
    }

    const start = moment.utc(value);
    const end = start.clone().add(1, "month");

    props.onChange(start, end, closeFilter);
  };

  const months = getMonthsList();
  const defaultValue = moment.utc(props.initialStart).startOf("month").valueOf();

  return <select defaultValue={defaultValue.toString()} onChange={e => { onChange(+e.currentTarget.value, true); }}>
    {months.map(({ year, entries }) => (
      <optgroup key={`quarter${year}`} label={year.toString()}>
        {entries.map(({ label, value }) =>
          <option key={`quarter${value}`} value={value}>{label}</option>
        )}
      </optgroup>
    ))}
  </select>;
};

export const PerWeek = (props: ISubPeriodProps) => {
  const weekOptions = Object.keys(site.yearsWithWeeks)
    .map(year => (<optgroup key={year} label={year}>
      {(site.yearsWithWeeks[year] as any as number[])
        .map((time, idx) => <option key={time} value={time}>{RESX.DateTime.Week} {idx + 1} {year}</option>)}
    </optgroup>));

  const onChange = (value: number, closeFilter: boolean) => {
    if (value === 0) {
      props.onChange(zeroMoment, zeroMoment, closeFilter);
      return;
    }

    const start = moment.utc(value);
    const end = start.clone().add(1, "week");

    props.onChange(start, end, closeFilter);
  };

  const defaultValue = moment.utc(props.initialStart).startOf("week").valueOf();

  return <select defaultValue={defaultValue.toString()} onChange={e => { onChange(+e.currentTarget.value, true); }}>
    {weekOptions}
  </select>;
};

const PerDay = (props: ISubPeriodProps) => {
  const [date, setDate] = useState<Moment>(moment.utc(props.initialStart).startOf("day"));

  const onChange = (newDate: Moment, closeFilter: boolean) => {
    setDate(newDate);

    const beginMoment = newDate.clone().startOf("day");
    const endMoment = newDate.clone().endOf("day");

    props.onChange(beginMoment, endMoment, closeFilter);
  };

  return <FilterDatePicker
    minDate={moment.utc(serverReference.constants.appointment.minDate)}
    maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
    onChange={(date, fromCalendar) => { onChange(date, fromCalendar); }}
    value={date}
  />;
};

const From = (props: ISubPeriodProps) => {
  const [date, setDate] = useState<Moment>(moment.utc(props.initialStart).startOf("day"));

  const onChange = (newDate: Moment, closeFilter: boolean) => {
    setDate(newDate);

    const beginMoment = newDate.clone().startOf("day");
    const endMoment = moment.utc(props.initialEnd);

    props.onChange(beginMoment, endMoment, closeFilter);
  };

  return <FilterDatePicker
    minDate={moment.utc(serverReference.constants.appointment.minDate)}
    maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
    onChange={(d, fromCalendar) => { onChange(d, fromCalendar); }}
    value={date}
  />;
};

const UntilAndIncluding = (props: ISubPeriodProps) => {
  const [date, setDate] = useState<Moment>(moment.utc(props.initialEnd).endOf("day"));
  const onChange = (newDate: Moment, closeFilter: boolean) => {
    setDate(newDate);
    const beginMoment = moment(props.initialStart);
    const endMoment = newDate.clone().endOf("day");

    props.onChange(beginMoment, endMoment, closeFilter);
  };

  return <FilterDatePicker
    minDate={moment.utc(serverReference.constants.appointment.minDate)}
    maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
    value={date}
    onChange={(d, fromCalendar) => { onChange(d, fromCalendar); }}
  />;
};

const FromUntilAndIncluding = (props: ISubPeriodProps) => {
  const [beginMoment, setBeginMoment] = useState<Moment>(moment.utc(props.initialStart).clone().startOf("day"));
  const [endMoment, setEndMoment] = useState<Moment>(props.initialEnd ? moment.utc(props.initialEnd) : beginMoment.clone().add(1, "day").endOf("day"));

  const onBeginChange = (newDate: Moment) => {
    const begin = newDate.clone().startOf("day");
    let newEndMoment = endMoment;
    if (begin.isAfter(endMoment)) {
      newEndMoment = begin.clone().endOf("day");
      setEndMoment(newEndMoment);
    }
    setBeginMoment(begin);
    props.onChange(begin, newEndMoment, false);
  };

  const onEndChange = (newDate: Moment) => {
    const end = newDate.clone().endOf("day");
    let newBeginMoment = beginMoment;
    if (end.isBefore(beginMoment)) {
      newBeginMoment = end.clone().startOf("day");
      setBeginMoment(newBeginMoment);
    }
    setEndMoment(end);
    props.onChange(newBeginMoment, end, false);
  };

  return <div className="flex">
    <FilterRangeStartDatePicker
      minDate={moment.utc(serverReference.constants.appointment.minDate)}
      maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
      value={beginMoment}
      rangeEnd={endMoment}
      onChange={onBeginChange}
    />

    <FilterRangeEndDatePicker
      dontUpdateOnInputChange
      minDate={moment.utc(serverReference.constants.appointment.minDate)}
      maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
      value={endMoment}
      rangeStart={beginMoment}
      onChange={onEndChange}
    />
  </div>;
};

const PeriodFilterSubPeriod = (props: IProps) => {
  switch (props.periodFilter) {
    case "perYear": return <PerYear onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "perQuarter": return <PerQuarter onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "perMonth": return <PerMonth onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "perWeek": return <PerWeek onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "perDay": return <PerDay onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "from": return <From onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "untilAndIncluding": return <UntilAndIncluding onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "fromUntilAndIncluding": return <FromUntilAndIncluding onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    default: throw new Error("Unrecognized period filter: " + props.periodFilter);
  }
};

export default PeriodFilterSubPeriod;